import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';


import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/basic';
import { UsersService } from '@data-access/user/services/user.service';
import { firstValueFrom, Observable } from 'rxjs';
import {
  CreateUserPayload,
  LoggedUser,
  LoginResponse,
  SubscriptionPlan,
  UserProfileResponse,
  UserRole
} from '@shared/interfacres/user';
import { AuthService } from '@data-access/user/services/auth.service';
import { mapToUserProfile } from '@shared/helpers/user-form-to-payload';
import { map } from 'rxjs/operators';
import { ContactUsPayload, DMCAPayload } from '@shared/interfacres/common';
import { PlayerSourceService } from '@shared/components/player-bar/player-source.service';


interface UsersState {
  user: Partial<LoggedUser> | null;
  initialMessageShown: boolean;
  name: string;
  isLoading: boolean;
  filter: GetFilter;
  pagination: GetPagination;
}

const initialState: UsersState = {
  user: null,
  initialMessageShown: false,
  name: '',
  isLoading: false,
  filter: { query: '', order: 'asc' },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
};

export const UsersStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(UsersService), snackBar = inject(MatSnackBar), auth = inject(AuthService), player = inject(PlayerSourceService)) => ({

    async initWithUser() {
      try {
        const user: UserProfileResponse = await firstValueFrom(service.getUser());
        if (user) {
          patchState(store, () => ({
            user: mapToUserProfile(user)
          }));
          return true;
        } else {
          return false;
        }
      } catch (e) {
        console.error('users.store initWithUser: Catch ', e);
        auth.logout();
        return false;
      }
    },

    getPerformanceTypes() {
      return service.getPerformanceTypes();
    },
    async create(payload: CreateUserPayload) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.create(payload));
        return res;

      } catch (e) {
        snackBar.open('Error during creation', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async update(payload: Partial<CreateUserPayload>) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.update(payload));

        const user: UserProfileResponse = await firstValueFrom(service.getUser());
        if (user) {
          patchState(store, () => ({
            user: mapToUserProfile(user)
          }));

        } else {

          this.logout();
          return {status: 'error', error: 'User not found'};
        }
        return res;

      } catch (e) {
        snackBar.open('Error during save', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async changePassword(oldPassword: string, newPassword: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.changePassword(oldPassword, newPassword));

        return res;

      } catch (e) {
        snackBar.open('Error during save', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async cancelSubscription() {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        return await firstValueFrom(service.cancelSubscription());

      } catch (e) {
        snackBar.open('Canceling error', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async changeSubscriptionPlan(productId: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.changeSubscriptionPlan(productId));
        snackBar.open('Success', (res as {message: string} ).message, { duration: 20000, horizontalPosition: 'end', panelClass: 'success-toast' });
        return res;

      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    getPlans(): Observable<SubscriptionPlan[]> {

      const namesMap: Record<string, string> = {
        1: 'Monthly',
        3: 'Quarterly',
        12: 'Yearly',
      };
      return service.getPlans().pipe(map((plans: SubscriptionPlan[]) => {
        const basePlan = plans.find((plan) => plan.product?.name?.toLowerCase() === 'standard');
        const basePlanFullCost = basePlan!.price * 12;
        const updated = plans.map((plan: SubscriptionPlan) => {

          let saveString = '';
          let monthly = 0;

          if (plan.interval === 'month') {
            monthly = (plan.price) / plan.interval_count;
          }
          if (plan.interval === 'year') {
            monthly = (plan.price) / (12 * plan.interval_count);
          }
          const currentPlanCost = monthly * 12;
          const nameIndex = plan.interval === 'month' ? plan.interval_count : plan.interval_count * 12;
          const newName = nameIndex ? namesMap[nameIndex] : '';
          const diff = (basePlanFullCost - currentPlanCost) / 100;
          const diffPercents = 100 - (currentPlanCost / basePlanFullCost) * 100;
          saveString = diffPercents > 0 ? `SAVE ${diffPercents}% ($${diff.toFixed(2)})` : '';
          return {
            ...plan,
            product: {
              ...plan.product,
              name: newName
            },
            monthly,
            saveString
          }
        });

        return updated;

      }));
    },

    getCheckoutLink(productId: string, email: string) {
      return service.getCheckoutLink(productId, email)
    },

    async getBillingHistory() {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.getBillingHistory());

        return res;
      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async getCurrentSubscription(hideError = false) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.getCurrentSubscription());

        return res;
      } catch (e) {
        if (!hideError) {
          snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        }

        console.error((e as { error : {message: string} }).error.message);
        return e;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async startSubscription(productId: string, promoCode: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.startSubscription(productId, promoCode));
        // snackBar.open('Success', (res as {message: string} ).message, { duration: 20000, horizontalPosition: 'end', panelClass: 'success-toast' });
        return res;

      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async resetPassword(email: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res =  await firstValueFrom(service.resetPassword(email))
        snackBar.open('Success', (res as {message: string} ).message, { duration: 20000, horizontalPosition: 'end', panelClass: 'success-toast' });

        return res;
      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async setPassword(password: string, accessToken: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        return await firstValueFrom(service.setPassword(password, accessToken))

      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async contactUs(payload: ContactUsPayload) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.contactUs(payload));
        if (res && res.success) {
          snackBar.open('Success', res.message, { duration: 5000, horizontalPosition: 'end', panelClass: 'success-toast' });
          return res;
        }
        snackBar.open('Error', res.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        return false;
      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async sendDMCA(payload: DMCAPayload) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.sendDMCA(payload));

        if (res && res.success) {
          snackBar.open('Success', res.message, { duration: 5000, horizontalPosition: 'end', panelClass: 'success-toast' });
          return res;

        }
        snackBar.open('Error', res.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        return false;

      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error((e as { error : {message: string} }).error.message);
        return false;
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async login(email: string, password: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res: LoginResponse = await firstValueFrom(service.login(email, password));
        auth.setTokens(res.session.access_token, res.session.refresh_token);

        const user: UserProfileResponse = await firstValueFrom(service.getUser());
        if (user) {
          patchState(store, () => ({
            user: mapToUserProfile(user)
          }));
          return {status: 'ok', error: ''};
        } else {

          this.logout();
          return {status: 'error', error: 'User not found'};
        }
      } catch (e) {
        snackBar.open('Login error', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        return {status: 'error', error: (e as { error : {message: string} }).error?.message};
      } finally {
        patchState(store, () => ({
          isLoading: false,
        }));
      }

    },

    async setUserData() {
      const user: UserProfileResponse = await firstValueFrom(service.getUser());
      if (user) {
        patchState(store, () => ({
          user: {
            id: user.id,
            email: user.email,
            role: user.role === 'admin' ? UserRole.Admin : UserRole.Authenticated,
            firstName: user.firstName,
            lastName: user.lastName,
            stageName: user.stageName,
            // performanceType: user.performanceType,
            performanceType: user.performanceType,
            performanceLocations: user.performanceLocations,
            weeklyPerformances: user.weeklyPerformances,
            socialLinks: {
              website: user.socialLinks.website || '',
              instagram: user.socialLinks.instagram || '',
              facebook: user.socialLinks.facebook || '',
            },
          }
        }));
      }
    },

    setInitialMessage(isShown: boolean) {
      patchState(store, () => ({
        initialMessageShown: isShown
      }));
    },

    async logout() {
      patchState(store, () => ({
        user: null,
        initialMessageShown: false
      }));
      player.clear();
      auth.logout();
    }
  }))
);
